import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const [validating, setValidating] = useState(true);
    const [validation, setValidation] = useState(null); // { user_email, user_name }
    const [validationError, setValidationError] = useState(null);

    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            setValidating(false);
            setValidationError("Geen token in de link gevonden.");
            return;
        }
        api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
            .then(({ data }) => setValidation(data))
            .catch((e) => setValidationError(e.response?.data?.detail || "Reset link ongeldig of verlopen"))
            .finally(() => setValidating(false));
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (pwd.length < 6) return toast.error("Wachtwoord moet minstens 6 tekens lang zijn");
        if (pwd !== pwd2) return toast.error("Wachtwoorden komen niet overeen");
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            setDone(true);
            toast.success("Wachtwoord ingesteld — je kan nu inloggen");
            setTimeout(() => navigate("/login"), 2500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout bij wijzigen wachtwoord");
        }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body flex items-center justify-center px-4 py-10" data-testid="reset-password-page">
            <div className="w-full max-w-md bg-[#FFFFFF] border border-zinc-100 rounded-md p-8">
                <div className="flex items-center gap-2 mb-6">
                    <KeyRound className="h-5 w-5 text-[#E5A93D]" />
                    <h1 className="font-display font-black text-2xl tracking-tight">Nieuw wachtwoord instellen</h1>
                </div>

                {validating && (
                    <div className="text-zinc-500 text-sm py-8 text-center">Reset link controleren...</div>
                )}

                {!validating && validationError && (
                    <div className="space-y-4" data-testid="reset-error-state">
                        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-red-700">{validationError}</div>
                        </div>
                        <p className="text-xs text-zinc-500">
                            Vraag aan Social Factory om een nieuwe reset link te genereren.
                        </p>
                        <Link to="/login" className="block w-full text-center h-11 leading-[44px] border border-zinc-200 hover:bg-zinc-100 rounded-sm text-sm" data-testid="back-to-login-error">
                            Terug naar inloggen
                        </Link>
                    </div>
                )}

                {!validating && validation && !done && (
                    <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
                        <p className="text-sm text-zinc-500">
                            Account: <span className="text-zinc-950 font-medium">{validation.user_name || validation.user_email}</span>
                        </p>
                        <div>
                            <Label htmlFor="pwd" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Nieuw wachtwoord</Label>
                            <div className="relative">
                                <Input
                                    id="pwd"
                                    type={showPwd ? "text" : "password"}
                                    value={pwd}
                                    onChange={(e) => setPwd(e.target.value)}
                                    minLength={6}
                                    required
                                    className="bg-[#F4F4F5] border-zinc-200 pr-10 focus-visible:border-[#E5A93D]"
                                    data-testid="new-password-input"
                                    placeholder="min. 6 tekens"
                                    autoFocus
                                />
                                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-950">
                                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="pwd2" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Bevestig wachtwoord</Label>
                            <Input
                                id="pwd2"
                                type={showPwd ? "text" : "password"}
                                value={pwd2}
                                onChange={(e) => setPwd2(e.target.value)}
                                minLength={6}
                                required
                                className="bg-[#F4F4F5] border-zinc-200 focus-visible:border-[#E5A93D]"
                                data-testid="confirm-password-input"
                            />
                        </div>
                        <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="submit-reset-btn">
                            {submitting ? "Bezig..." : "Wachtwoord instellen"}
                        </Button>
                    </form>
                )}

                {done && (
                    <div className="space-y-4 text-center py-4" data-testid="reset-success-state">
                        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
                        <div className="text-sm text-zinc-600">Wachtwoord succesvol ingesteld.</div>
                        <div className="text-xs text-zinc-500">Je wordt doorgestuurd naar de inlogpagina...</div>
                    </div>
                )}
            </div>
        </div>
    );
}
